import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../../services/api';
import { useWebSocket } from '../../contexts/WebSocketContext';

/**
 * NotificationBell — the in-app notification center (T12).
 *
 * Shows an unread badge and a dropdown of recent notifications (team/app invites,
 * accepted invites, access changes). Live: subscribes to the `notification` socket
 * event (server pushes to the user's room) so the badge updates without a refetch.
 * Clicking a notification marks it read and follows its link.
 *
 * Styled for the sidebar: a nav-item-shaped trigger that adapts to collapsed mode,
 * with the panel floating to the right (sidebar is left-docked).
 */

interface INotification {
  id?: string;
  _id?: string;
  type: string;
  payload?: {
    title?: string;
    body?: string;
    link?: string;
    actorName?: string;
    teamName?: string | null;
    appName?: string | null;
  };
  readAt?: string | null;
  createdAt?: string;
}

const nid = (n: INotification) => String(n.id || n._id || '');

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface NotificationBellProps {
  collapsed?: boolean;
  isMobile?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ collapsed = false, isMobile = false }) => {
  const navigate = useNavigate();
  const { subscribe, unsubscribe } = useWebSocket();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [items, setItems] = useState<INotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const showLabel = isMobile || !collapsed;

  const refreshCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.unreadCount();
      setUnread(res.data?.count || 0);
    } catch { /* badge is best-effort */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list();
      setItems(res.data?.notifications || []);
    } catch { /* surface nothing — empty state covers it */ }
    finally { setLoading(false); }
  }, []);

  // Initial badge + live updates via the `notification` socket push.
  useEffect(() => {
    refreshCount();
    const onPush = (n: INotification) => {
      setUnread((c) => c + 1);
      setItems((prev) => [{ ...n, readAt: null }, ...prev].slice(0, 30));
    };
    subscribe('notification', onPush);
    return () => unsubscribe('notification', onPush);
  }, [subscribe, unsubscribe, refreshCount]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
    else setShowAll(false);
  };

  const markRead = async (n: INotification) => {
    if (n.readAt) return;
    setItems((prev) => prev.map((x) => (nid(x) === nid(n) ? { ...x, readAt: new Date().toISOString() } : x)));
    setUnread((c) => Math.max(0, c - 1));
    try { await notificationsAPI.markRead(nid(n)); } catch { /* optimistic; badge re-syncs on next open */ }
  };

  const onClickItem = async (n: INotification) => {
    await markRead(n);
    setOpen(false);
    const link = n.payload?.link;
    if (link) navigate(link);
  };

  const markAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    setUnread(0);
    try { await notificationsAPI.markAllRead(); } catch { /* optimistic */ }
  };

  const dismiss = async (n: INotification) => {
    const id = nid(n);
    if (!n.readAt) setUnread((c) => Math.max(0, c - 1));
    setItems((prev) => prev.filter((x) => nid(x) !== id));
    try { await notificationsAPI.archive(id); } catch { /* optimistic */ }
  };

  const clearAll = async () => {
    setItems([]);
    setUnread(0);
    try { await notificationsAPI.archiveAll(); } catch { /* optimistic */ }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        title={(!isMobile && collapsed) ? 'Notifications' : undefined}
        className={`w-full flex items-center rounded-lg transition-colors duration-150 focus:outline-none ${
          (!isMobile && collapsed) ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
        } ${open ? 'bg-white/[0.04] text-white' : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'}`}
      >
        <span className="relative flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9M15 17H18.5905C18.973 17 19.1652 17 19.3201 16.9478C19.616 16.848 19.8475 16.6156 19.9473 16.3198C19.9997 16.1643 19.9997 15.9715 19.9997 15.5859C19.9997 15.4172 19.9995 15.3329 19.9863 15.2524C19.9614 15.1004 19.9024 14.9563 19.8126 14.8312C19.7651 14.7651 19.7048 14.7048 19.5858 14.5858L19.1963 14.1963C19.0706 14.0706 19 13.9001 19 13.7224V10C19 6.134 15.866 2.99999 12 3C8.13401 3.00001 5 6.13401 5 10V13.7224C5 13.9002 4.92924 14.0706 4.80357 14.1963L4.41406 14.5858C4.29476 14.7051 4.23504 14.765 4.1875 14.8312C4.09766 14.9564 4.03815 15.1004 4.0132 15.2524C4 15.3329 4 15.4172 4 15.586C4 15.9715 4 16.1642 4.05245 16.3197C4.15225 16.6156 4.3848 16.848 4.68066 16.9478C4.83556 17 5.02701 17 5.40956 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {unread > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-white font-semibold"
              style={{ background: '#3264F8', minWidth: 16, height: 16, fontSize: 10, padding: '0 4px', lineHeight: 1 }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        {showLabel && <span className="text-sm font-medium whitespace-nowrap">Notifications</span>}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="Notifications"
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            className="absolute z-50 rounded-xl border overflow-hidden flex flex-col"
            style={{
              background: '#121212',
              borderColor: 'rgba(255,255,255,0.08)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)',
              fontFamily: 'DM Sans, sans-serif',
              bottom: 'calc(100% + 8px)',
              left: 0,
              maxHeight: 420,
              ...(isMobile ? { right: 0 } : { width: 320 }),
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <span className="text-sm font-semibold text-white">Notifications</span>
              <div className="flex items-center gap-3">
                {items.some((n) => !n.readAt) && (
                  <button
                    type="button"
                    onClick={markAll}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                {items.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-y-auto" style={{ flex: '1 1 auto', minHeight: 0 }}>
              {loading ? (
                <div className="px-4 py-6 text-sm text-[#a3a3a3] text-center">Loading…</div>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-sm text-[#a3a3a3] text-center">You're all caught up.</div>
              ) : (
                <>
                {items.slice(0, showAll ? items.length : 4).map((n) => {
                  const unreadItem = !n.readAt;
                  return (
                    <div
                      key={nid(n)}
                      className="group relative flex gap-3 px-4 py-3 transition-colors hover:bg-white/[0.04] border-b"
                      style={{ borderColor: 'rgba(255,255,255,0.04)' }}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => onClickItem(n)}
                        className="flex gap-3 text-left min-w-0 flex-1"
                      >
                        <span
                          className="mt-1.5 flex-shrink-0 rounded-full"
                          style={{ width: 7, height: 7, background: unreadItem ? '#3264F8' : 'transparent' }}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block text-sm ${unreadItem ? 'text-white font-medium' : 'text-gray-300'}`}>
                            {n.payload?.title || 'New notification'}
                          </span>
                          {n.payload?.body && (
                            <span className="block text-xs text-[#a3a3a3] mt-0.5 truncate">{n.payload.body}</span>
                          )}
                          <span className="block text-[11px] text-[#8a8a8a] mt-1">{timeAgo(n.createdAt)}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={(e) => { e.stopPropagation(); dismiss(n); }}
                        className="touch-reveal inline-flex items-center justify-center max-md:min-h-11 max-md:min-w-11 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-[#a3a3a3] hover:text-white hover:bg-white/[0.08]"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    </div>
                  );
                })}
                {!showAll && items.length > 4 && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="w-full px-4 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors text-center flex-shrink-0"
                  >
                    See {items.length - 4} more
                  </button>
                )}
                {showAll && items.length > 4 && (
                  <button
                    type="button"
                    onClick={() => setShowAll(false)}
                    className="w-full px-4 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors text-center"
                  >
                    Show less
                  </button>
                )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
